import {
    Component,
    computed,
    effect,
    inject,
    input,
    OnInit,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { Message } from 'primeng/message';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import {
    CategoryType,
    GameTemplate,
    SiteTemplate,
    WinMode,
} from '@/lib-backend';
import { SiteTemplateService } from '@/app/services/site-template.service';
import { ProductAssetService } from '@/app/services/product-asset.service';
import { SharedContentService } from '@/app/services/shared-content.service';
import { NotificationService } from '@/app/services/notification.service';
import { AuthService } from '@/app/services/auth.service';
import { BrandKit, BrandKitService } from '@/app/services/brand-kit.service';
import { AiAssistService } from '@/app/services/ai-assist.service';
import {
    AiBaseArtGenerate,
    BaseArtPick,
    GamePlateType,
} from '@/app/components/ai-base-art-generate';
import { AiBaseArtBulk } from '@/app/components/ai-base-art-bulk';
import { ImagePreviewService } from '@/app/services/image-preview.service';
import {
    hashPathToken,
    sharedContentPreviewUrl,
} from '@/app/utils/shared-content-url';

/** One editable art slot. `value` is the stored (relative) URL; `preview` is
 *  what the `<img>` shows — an object URL right after an upload, otherwise the
 *  stored URL resolved for display. `preset` slots (icon/banner) stay visible
 *  even when empty; non-preset (legacy) slots disappear once cleared. */
interface ArtSlot {
    name: string;
    value: string;
    preview: string;
    busy: boolean;
    preset: boolean;
}

const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/** Art slots every game is expected to have; always shown (lowercase). */
const PRESET_SLOT_NAMES = ['icon', 'banner'];

const CATEGORY_LABELS: Partial<Record<CategoryType, string>> = {
    [CategoryType.SkillBasedGames]: 'Skill-based games',
    [CategoryType.LuckyBasedGames]: 'Lucky-based games',
};
const CATEGORY_ORDER: CategoryType[] = [
    CategoryType.SkillBasedGames,
    CategoryType.LuckyBasedGames,
];

interface SiteTemplateGroup {
    label: string;
    items: { label: string; value: string }[];
}

/**
 * Per-(site template, game template) base-art editor — the text-free `icon` /
 * `banner` plates a site template overrides a game template with. Reusable body
 * shared by the standalone Base Art dialog and the Game Template editor's
 * Base Art tab. Every change auto-saves: a picked/generated image is upserted
 * immediately, clearing a slot removes it — no explicit Save step.
 */
@Component({
    selector: 'app-product-base-art-panel',
    standalone: true,
    imports: [
        FormsModule,
        Button,
        Message,
        Select,
        Tooltip,
        AiBaseArtGenerate,
        AiBaseArtBulk,
    ],
    templateUrl: './product-base-art-panel.html',
})
export class ProductBaseArtPanel implements OnInit {
    /** The game template whose base art is edited. */
    readonly template = input<GameTemplate | null>(null);
    /** When false the panel does not load (e.g. an inactive tab / closed dialog). */
    readonly active = input<boolean>(true);

    protected readonly siteTemplateService = inject(SiteTemplateService);
    private readonly productAsset = inject(ProductAssetService);
    private readonly sharedContent = inject(SharedContentService);
    private readonly notification = inject(NotificationService);
    private readonly auth = inject(AuthService);
    private readonly brandKitService = inject(BrandKitService);
    private readonly aiAssist = inject(AiAssistService);
    protected readonly preview = inject(ImagePreviewService);

    /** Brand-kit of the selected site template — feeds the AI generator. */
    protected readonly brandKit = signal<BrandKit | null>(null);

    protected readonly aiVisible = signal(false);
    protected readonly aiAssetTypes = signal<GamePlateType[]>([]);

    /** Bulk "generate across all compatible templates" window. */
    protected readonly bulkVisible = signal(false);

    protected readonly selectedSiteTemplateId = signal<string | null>(null);
    protected readonly slots = signal<ArtSlot[]>([]);
    protected readonly loading = signal(false);

    protected readonly siteTemplates = this.siteTemplateService.templates;

    /**
     * The category a site template must have to be compatible with this game.
     * Mirrors the game-tab bridge (WinMode ↔ CategoryType); null when the game
     * template's WinMode is unresolved — in that case no template is compatible.
     */
    protected readonly compatibleCategory = computed<CategoryType | null>(() => {
        switch (this.template()?.GameKind?.WinMode) {
            case WinMode.SkillBased:
                return CategoryType.SkillBasedGames;
            case WinMode.ChanceBased:
                return CategoryType.LuckyBasedGames;
            default:
                return null;
        }
    });

    /** Compatible site templates, grouped by category then sorted by name. */
    protected readonly siteTemplateGroups = computed<SiteTemplateGroup[]>(() => {
        const wanted = this.compatibleCategory();
        if (wanted === null) return [];
        const visible = this.siteTemplates().filter(
            (st: SiteTemplate) => st.CategoryType === wanted,
        );
        const byCategory = new Map<CategoryType, SiteTemplate[]>();
        for (const st of visible) {
            const bucket = byCategory.get(st.CategoryType) ?? [];
            bucket.push(st);
            byCategory.set(st.CategoryType, bucket);
        }
        const categories = [
            ...CATEGORY_ORDER.filter((c) => byCategory.has(c)),
            ...[...byCategory.keys()].filter((c) => !CATEGORY_ORDER.includes(c)),
        ];
        return categories.map((category) => ({
            label: CATEGORY_LABELS[category] ?? category,
            items: byCategory
                .get(category)!
                .slice()
                .sort((a, b) => a.Name.localeCompare(b.Name))
                .map((st) => ({ value: st.SiteTemplateId, label: st.Name })),
        }));
    });

    protected readonly hasSiteTemplates = computed(() =>
        this.siteTemplateGroups().some((g) => g.items.length > 0),
    );

    protected readonly filterHint = computed<string | null>(() => {
        const wanted = this.compatibleCategory();
        if (wanted === null) {
            return "This game's win mode is unresolved, so no compatible site template can be determined.";
        }
        return `Showing only ${CATEGORY_LABELS[wanted] ?? wanted} templates compatible with this game.`;
    });

    /** Uploads land in the shared content store — global admins only. */
    protected readonly canUpload = computed(() => this.auth.isGlobalAdmin());

    protected readonly anyBusy = computed(() =>
        this.slots().some((s) => s.busy),
    );

    constructor() {
        // (Re)load the stored art whenever the panel becomes active, the game
        // template changes, or the operator picks a different site template.
        effect(() => {
            const active = this.active();
            const siteTemplateId = this.selectedSiteTemplateId();
            const template = this.template();
            if (!active || !siteTemplateId || !template) {
                if (active && !siteTemplateId) this.slots.set([]);
                return;
            }
            void this.loadArt(siteTemplateId, template.Id);
        });

        // Resolve the brand-kit (style + text-free references) of the selected
        // site template so the AI generator can style the art to the template.
        effect(() => {
            const siteTemplateId = this.selectedSiteTemplateId();
            this.brandKit.set(null);
            if (!siteTemplateId) return;
            void this.brandKitService.resolve(siteTemplateId).then((kit) => {
                if (this.selectedSiteTemplateId() === siteTemplateId) {
                    this.brandKit.set(kit);
                }
            });
        });
    }

    ngOnInit(): void {
        if (this.siteTemplates().length === 0) {
            void this.siteTemplateService.loadTemplates();
        }
        if (this.aiAssist.status() === 'unknown') {
            void this.aiAssist.refreshHealth();
        }
    }

    // ── AI base-art generation ──────────────────────────────────────────────

    /** `GameKind.Name` (fallback to the id) forwarded to the AI generator. */
    protected readonly aiGameKind = computed(() => {
        const t = this.template();
        return t ? (t.GameKind?.Name ?? t.GameKindId) : '';
    });

    /** Null when AI generation is available, otherwise the reason (for tooltips). */
    protected readonly aiReason = computed<string | null>(() => {
        if (!this.canUpload()) {
            return 'Only a global admin can generate base art.';
        }
        if (!this.selectedSiteTemplateId()) {
            return 'Select a site template first.';
        }
        if (this.aiAssist.status() === 'down') {
            return 'AI Assist service is unavailable.';
        }
        const kit = this.brandKit();
        if (!kit || kit.references.length === 0) {
            return 'The selected site template has no brand references for AI generation.';
        }
        return null;
    });

    protected readonly aiDisabled = computed(() => this.aiReason() !== null);

    /** `icon` / `banner` if the slot maps to a generatable plate, else null. */
    protected gamePlateType(name: string): GamePlateType | null {
        const n = name.trim().toLowerCase();
        return n === 'icon' || n === 'banner' ? n : null;
    }

    protected openAi(types: GamePlateType[]): void {
        if (this.aiDisabled() || types.length === 0) return;
        this.aiAssetTypes.set(types);
        this.aiVisible.set(true);
    }

    protected openBulk(): void {
        if (!this.canUpload() || !this.template()) return;
        this.bulkVisible.set(true);
    }

    /** A bulk save may have touched the currently-selected template — reload. */
    protected onBulkChanged(): void {
        const siteTemplateId = this.selectedSiteTemplateId();
        const template = this.template();
        if (siteTemplateId && template) {
            void this.loadArt(siteTemplateId, template.Id);
        }
    }

    /** Re-host each chosen AI candidate into the product-art path, then save. */
    protected async onAiApplied(picks: BaseArtPick[]): Promise<void> {
        const siteTemplateId = this.selectedSiteTemplateId();
        const template = this.template();
        if (!siteTemplateId || !template) return;

        for (const pick of picks) {
            this.ensureSlot(pick.assetType);
            this.patchSlot(pick.assetType, {
                busy: true,
                preview: pick.url,
            });
            const path = await this.slotStoragePath(pick.assetType);
            const rehosted = path
                ? await this.sharedContent.rehostImageUrl(path, pick.url)
                : null;
            if (rehosted) {
                await this.persistSlotValue(pick.assetType, rehosted, pick.url);
            } else {
                this.patchSlot(pick.assetType, { busy: false });
            }
        }
    }

    // ── Slots ────────────────────────────────────────────────────────────────

    private async loadArt(
        siteTemplateId: string,
        gameTemplateId: string,
    ): Promise<void> {
        this.loading.set(true);
        try {
            const art = await this.productAsset.getGameTemplateBaseArt(
                siteTemplateId,
                gameTemplateId,
            );
            // The picker may have changed while awaiting — ignore a stale load.
            if (this.selectedSiteTemplateId() !== siteTemplateId) return;
            const stored = art?.Properties ?? [];
            const slots: ArtSlot[] = [];
            for (const preset of PRESET_SLOT_NAMES) {
                const found = stored.find(
                    (p) => p.Name.trim().toLowerCase() === preset,
                );
                slots.push({
                    name: preset,
                    value: found?.Value ?? '',
                    preview: found ? this.resolvePreview(found.Value) : '',
                    busy: false,
                    preset: true,
                });
            }
            for (const p of stored) {
                if (PRESET_SLOT_NAMES.includes(p.Name.trim().toLowerCase()))
                    continue;
                slots.push({
                    name: p.Name,
                    value: p.Value,
                    preview: this.resolvePreview(p.Value),
                    busy: false,
                    preset: false,
                });
            }
            this.slots.set(slots);
        } finally {
            if (this.selectedSiteTemplateId() === siteTemplateId) {
                this.loading.set(false);
            }
        }
    }

    protected async onFilePicked(
        name: string,
        eventTarget: EventTarget | null,
    ): Promise<void> {
        const input = eventTarget as HTMLInputElement;
        const file = input.files?.[0];
        // Allow re-picking the same file after a failed attempt.
        input.value = '';
        const slot = this.slots().find((s) => s.name === name);
        if (!file || !slot || slot.busy) return;

        if (file.size > MAX_UPLOAD_BYTES) {
            const limitMb = (MAX_UPLOAD_BYTES / (1024 * 1024)).toFixed(1);
            this.notification.show({
                severity: 'warn',
                summary: 'File too large',
                detail: `"${file.name}" exceeds the ${limitMb} MB limit.`,
            });
            return;
        }

        const path = await this.slotStoragePath(name);
        if (!path) return;

        const localPreview = URL.createObjectURL(file);
        this.patchSlot(name, { busy: true, preview: localPreview });
        const url = await this.sharedContent.uploadImageFile(path, file);
        if (url) {
            await this.persistSlotValue(name, url, localPreview);
        } else {
            // Upload failed — drop the optimistic preview.
            this.patchSlot(name, {
                busy: false,
                preview: this.resolvePreview(slot.value),
            });
        }
    }

    /** Upsert a single slot value and reflect the result, or revert on failure. */
    private async persistSlotValue(
        name: string,
        value: string,
        preview: string,
    ): Promise<void> {
        const siteTemplateId = this.selectedSiteTemplateId();
        const template = this.template();
        if (!siteTemplateId || !template) return;
        const { result } = await this.productAsset.updateGameTemplateBaseArt(
            siteTemplateId,
            template.Id,
            [{ Name: name, Value: value }],
            [],
        );
        if (result === 'Ok') {
            this.patchSlot(name, { value, preview, busy: false });
            this.notification.show({
                severity: 'success',
                summary: 'Base art saved',
            });
        } else {
            this.patchSlot(name, { busy: false });
            this.notification.show({
                severity: 'error',
                summary: 'Failed to save base art',
            });
        }
    }

    /** Clear a slot — removes the stored property (save null). */
    protected async clearSlot(name: string): Promise<void> {
        const siteTemplateId = this.selectedSiteTemplateId();
        const template = this.template();
        const slot = this.slots().find((s) => s.name === name);
        if (!siteTemplateId || !template || !slot || slot.busy) return;
        if (!slot.value) {
            // Nothing stored — just drop a non-preset empty row locally.
            if (!slot.preset) this.removeSlotRow(name);
            return;
        }
        this.patchSlot(name, { busy: true });
        const { result } = await this.productAsset.updateGameTemplateBaseArt(
            siteTemplateId,
            template.Id,
            [],
            [name],
        );
        if (result === 'Ok') {
            if (slot.preset) {
                this.patchSlot(name, { value: '', preview: '', busy: false });
            } else {
                this.removeSlotRow(name);
            }
            this.notification.show({
                severity: 'success',
                summary: 'Base art cleared',
            });
        } else {
            this.patchSlot(name, { busy: false });
            this.notification.show({
                severity: 'error',
                summary: 'Failed to clear base art',
            });
        }
    }

    private ensureSlot(name: string): void {
        if (this.slots().some((s) => s.name === name)) return;
        this.slots.update((list) => [
            ...list,
            {
                name,
                value: '',
                preview: '',
                busy: false,
                preset: PRESET_SLOT_NAMES.includes(name),
            },
        ]);
    }

    private patchSlot(name: string, patch: Partial<ArtSlot>): void {
        this.slots.update((list) =>
            list.map((s) => (s.name === name ? { ...s, ...patch } : s)),
        );
    }

    private removeSlotRow(name: string): void {
        this.slots.update((list) => list.filter((s) => s.name !== name));
    }

    /**
     * Resolve a stored base-art URL for preview. Base art is site-template
     * scoped (no single realm), so relative `/content/...` URLs resolve straight
     * to the shared-content bucket; absolute URLs pass through unchanged.
     */
    private resolvePreview(url: string): string {
        return sharedContentPreviewUrl(url.trim());
    }

    /**
     * Shared content store path for a slot's image — the single source of truth
     * for both manual upload and AI re-host. The site template is hashed so its
     * readable slug never appears in the path.
     */
    private async slotStoragePath(slotName: string): Promise<string | null> {
        const siteTemplateId = this.selectedSiteTemplateId();
        const template = this.template();
        if (!siteTemplateId || !template) return null;
        const slug = this.slotSlug(slotName);
        const hashed = await hashPathToken(siteTemplateId);
        return `products/${template.Id}/${hashed}/${slug}-base`;
    }

    private slotSlug(name: string): string {
        const slug = name
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
        return slug || 'slot';
    }
}
